import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { VendorService } from '../../services/vendor.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { PaymentsService } from '../../services/payments.service';

/**
 * Vendor Statement Reconciliation — tab inside the vendor view.
 * Deterministic engine is server-side (apir/vendor/statement); this only drives it.
 */
@Component({
	selector: 'app-vendor-statement-recon',
	templateUrl: './vendor-statement-recon.component.html',
	styleUrls: ['./vendor-statement-recon.component.scss']
})
export class VendorStatementReconComponent implements OnInit, OnChanges {
	@Input() vendor: any;

	statementDate: string = new Date().toISOString().slice(0, 10);
	dueDate: string = '';
	accountRef = '';
	fileName = '';
	private fileData = '';            // data URI (base64)

	running = false;
	applying = false;
	loadingConfig = false;
	loadingRuns = false;
	loadingRun = false;
	loadingIssues = false;
	loadingLines = false;
	loadingPayables = false;
	loadingFixes = false;
	run: any = null;                  // run header + summary
	issues: any[] = [];               // issue/action summary
	lines: any[] = [];                // lines for the selected issue (or all)
	payables: any[] = [];
	runsList: any[] = [];
	fixes: any[] = [];
	selectedLineIds: any = {};
	private loadedSupplierno = '';
	activeIssue: string | null = null;
	payablesReviewOnly = false;
	subTab: 'summary' | 'issues' | 'raw' | 'payables' | 'ready' = 'summary';

	// human labels
	issueLabels: any = {
		not_in_v8: 'Not in V8', settled_v8_open_stmt: 'Settled in V8, open on statement',
		amount_mismatch: 'Amount mismatch', duedate_mismatch: 'Due-date mismatch',
		booked_reformatted: 'Ref mismatch',
		accounting_credit: 'Accounting credit', dup_pair: 'Credit/invoice pair', clean: 'Clean',
		pre_cutover: 'Before V8 cutover (carried)'
	};
	actionLabels: any = {
		none: '—', change_ref: 'Change ref', book_if_valid: 'Book if valid', accounting_pickup: 'Accounting pickup',
		verify_amount_date: 'Verify same amount/date', reopen_owner: 'Reopen', due_date_fix: 'Due date fix'
	};

	constructor(private vendorService: VendorService, private gss: GlobalSearchService, private printService: PrintService, private paymentsService: PaymentsService, private router: Router) {}

	ngOnInit(): void {
		this.loadVendorContext();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.vendor) {
			this.loadVendorContext();
		}
	}

	private loadVendorContext(): void {
		const supplierno = this.supplierno;
		if (!supplierno || supplierno === this.loadedSupplierno) {
			return;
		}
		this.loadedSupplierno = supplierno;
		this.run = null;
		this.issues = [];
		this.lines = [];
		this.payables = [];
		this.runsList = [];
		this.fixes = [];
		this.clearSelectedLines();
		this.paySelection = {};
		this.reviewSelection = {};
		this.generatedRun = null;
		this.payDueCutoff = '';
		this.configExists = false;
		this.configLoaded = false;
		this.loadRuns();
		this.loadPayables();
		this.loadConfig();
		this.loadBankAccounts();
	}

	/* ----- vendor config editor ----- */
	showSetup = false;
	configExists = false;
	configLoaded = false;
	savingConfig = false;
	private bucketMap: any = {};   // preserved (age_code map) — edited only via advanced
	cfg: any = {
		vendor_label: '', parser_profile: '', terms_code: '', active: true,
		prefixes: '', suffix_variants: '', allow_raw: true,
		terms_months: 1, terms_day: 10,
		bucket_by: 'due_date', credits_into_current: false, start_date: '',
		sf_due_date: true, sf_ref: true, sf_reopen: false,
		raw_tab: 'Statement raw', issues_tab: 'Issues', ledger_tab: 'V8 Payables',
		credit_notes: ''
	};

	loadConfig(): void {
		this.loadingConfig = true;
		this.vendorService.stmtConfigGet(this.supplierno).subscribe((res: any) => {
			this.loadingConfig = false;
			const c = res && res.config;
			this.configExists = !!c;
			this.configLoaded = true;
			if (!c) { this.cfg.vendor_label = this.vendorLabel(); return; }
			const rr = c.ref_rules || {}, tl = c.terms_logic || {}, bl = c.bucket_logic || {}, sf = c.safe_autofix || {}, wt = c.workbook_template || {}, cb = c.credit_behavior || {};
			this.bucketMap = bl.map || {};
			this.cfg = {
				vendor_label: c.vendor_label || this.vendorLabel(),
				parser_profile: c.parser_profile || '',
				terms_code: c.terms_code || '',
				active: c.active == 1 || c.active === true,
				prefixes: (rr.prefixes || []).join(', '),
				suffix_variants: (rr.suffix_variants || []).join(', '),
				allow_raw: rr.allow_raw !== false,
				terms_months: tl.months != null ? tl.months : 1,
				terms_day: tl.day != null ? tl.day : 10,
				bucket_by: bl.by || 'due_date',
				credits_into_current: !!bl.credits_into_current,
				start_date: bl.start_date || '',
				sf_due_date: sf.due_date !== false,
				sf_ref: sf.ref_correction !== false,
				sf_reopen: !!sf.reopen,
				raw_tab: wt.raw_tab || 'Statement raw',
				issues_tab: wt.issues_tab || 'Issues',
				ledger_tab: wt.ledger_tab || 'V8 Payables',
				credit_notes: cb.notes || ''
			};
		}, () => { this.loadingConfig = false; this.configLoaded = true; });
	}

	private csv(s: string): string[] {
		return (s || '').split(',').map(x => x.trim()).filter(x => x !== '');
	}

	saveConfig(): void {
		const c = this.cfg;
		const payload: any = {
			supplierno: this.supplierno,
			vendor_label: c.vendor_label,
			parser_profile: c.parser_profile,
			terms_code: c.terms_code,
			active: !!c.active,
			ref_rules: { prefixes: this.csv(c.prefixes), suffix_variants: this.csv(c.suffix_variants), allow_raw: !!c.allow_raw, strip_nonnumeric_fallback: true },
			terms_logic: { type: 'prox_month_day', months: +c.terms_months || 0, day: +c.terms_day || 0 },
			bucket_logic: Object.assign(
				// cutover applies to both bucketing modes — lines dated before it
				// predate V8 and are parked out of the work queue.
				{ start_date: (c.start_date || '').slice(0, 10) },
				c.bucket_by === 'age_code'
					? { by: 'age_code', map: Object.keys(this.bucketMap).length ? this.bucketMap : { '1': 'current', '0': 'future' } }
					: { by: 'due_date', credits_into_current: !!c.credits_into_current }
			),
			credit_behavior: { notes: c.credit_notes || '' },
			safe_autofix: { due_date: !!c.sf_due_date, ref_correction: !!c.sf_ref, reopen: !!c.sf_reopen },
			workbook_template: { raw_tab: c.raw_tab, issues_tab: c.issues_tab, ledger_tab: c.ledger_tab }
		};
		this.savingConfig = true;
		this.vendorService.stmtConfigSave(payload).subscribe((res: any) => {
			this.savingConfig = false;
			if (res && res.success) { this.note('Vendor config saved.', 'success'); this.configExists = true; this.showSetup = false; }
			else { this.note((res && res.error) ? res.error : 'Save failed.', 'error'); }
		}, () => { this.savingConfig = false; this.note('Save failed.', 'error'); });
	}

	get supplierno(): string {
		return this.vendor?.supplierid || this.vendor?.supplierno || '';
	}

	onDropzoneChange(ev: NgxDropzoneChangeEvent): void {
		if (!ev) return;
		if (ev.rejectedFiles && ev.rejectedFiles.length) { this.note('Not added — needs to be a PDF.', 'warning'); }
		const f = ev.addedFiles && ev.addedFiles[0];
		if (!f) return;
		if (f.type && f.type.indexOf('pdf') === -1) { this.note('Please drop a PDF statement.', 'warning'); return; }
		this.fileName = f.name;
		const reader = new FileReader();
		reader.onload = () => { this.fileData = String(reader.result || ''); };
		reader.readAsDataURL(f);
	}
	clearFile(): void { this.fileName = ''; this.fileData = ''; }

	runRecon(): void {
		if (!this.supplierno) { this.note('No vendor selected.', 'warning'); return; }
		if (!this.fileData) { this.note('Upload the statement PDF first.', 'warning'); return; }
		if (!this.dueDate) { this.note('Enter the due date — it sets the pay cutoff for this run.', 'warning'); return; }
		this.running = true;
		this.vendorService.stmtRun({
			supplierno: this.supplierno,
			statement_date: this.statementDate || null,
			due_date: this.dueDate || null,
			account_ref: this.accountRef || null,
			filename: this.fileName,
			content: this.fileData
		}).subscribe((res: any) => {
			this.running = false;
			if (res && res.success) {
				this.note('Reconciled: ' + res.total_lines + ' lines.', 'success');
				this.loadRun(res.run_id);
				this.loadRuns(false);
			} else {
				this.note((res && res.error) ? res.error : 'Reconciliation failed.', 'error');
			}
		}, () => { this.running = false; this.note('Reconciliation failed.', 'error'); });
	}

	loadRuns(openLatest = true): void {
		this.loadingRuns = true;
		this.vendorService.stmtRuns(this.supplierno).subscribe((r: any) => {
			this.loadingRuns = false;
			this.runsList = (r && r.runs) || [];
			if (openLatest && !this.run && this.runsList.length) {
				this.loadRun(this.runsList[0].id);
			}
		}, () => { this.loadingRuns = false; });
	}

	archiveRun(run: any, ev?: Event): void {
		if (ev) {
			ev.stopPropagation();
		}
		if (!run || !run.id) {
			return;
		}
		if (!confirm('Remove reconciliation run #' + run.id + ' from Recent? Audit rows are preserved.')) {
			return;
		}
		this.vendorService.stmtArchiveRun({ run_id: run.id, supplierno: this.supplierno }).subscribe((res: any) => {
			if (res && res.success) {
				const wasActive = this.run && Number(this.run.id) === Number(run.id);
				if (wasActive) {
					this.run = null;
					this.issues = [];
					this.lines = [];
					this.fixes = [];
					this.matchback = null;
					this.clearSelectedLines();
				}
				this.note('Removed run #' + run.id + ' from Recent.', 'success');
				this.loadRuns(wasActive);
			} else {
				this.note((res && res.error) ? res.error : 'Remove failed.', 'error');
			}
		}, () => { this.note('Remove failed.', 'error'); });
	}

	matchback: any = null;
	loadPayables(): void {
		const runId = this.run ? this.run.id : undefined;
		this.loadingPayables = true;
		this.reviewSelection = {};
		this.vendorService.stmtPayables(this.supplierno, !runId, runId).subscribe((r: any) => {
			this.loadingPayables = false;
			this.payables = (r && r.payables) || [];
			this.matchback = (r && r.matchback) || null;
		}, () => { this.loadingPayables = false; });
	}

	displayedPayables(): any[] {
		const rows = this.payables || [];
		return this.payablesReviewOnly ? rows.filter((p: any) => p.review == 1) : rows;
	}

	/* ----- "In V8, not on statement" review: bulk reverse verified human-error entries -----
	 * Everything here is already open+unsettled+not-on-statement (matchback flags it that way).
	 * The reviewer checks the ones they've confirmed are our mistakes (dupes, mis-entries) and
	 * reverses them in one pass via the same AP reversal engine as the single-row action; whatever
	 * is left unchecked is the genuine set for the owner/Ray to decide how to adjust the rec. */
	reviewSelection: any = {};
	bulkReversing = false;

	isReviewSelected(p: any): boolean {
		return !!(p && p.id != null && this.reviewSelection[p.id]);
	}

	setReviewSelected(p: any, ev: any): void {
		if (!p || p.id == null) { return; }
		if (ev && ev.target && ev.target.checked) {
			this.reviewSelection[p.id] = true;
		} else {
			delete this.reviewSelection[p.id];
		}
	}

	allReviewSelected(): boolean {
		const rows = this.displayedPayables();
		return rows.length > 0 && rows.every((p: any) => this.isReviewSelected(p));
	}

	toggleAllReview(ev: any): void {
		const checked = !!(ev && ev.target && ev.target.checked);
		this.displayedPayables().forEach((p: any) => {
			if (checked) { this.reviewSelection[p.id] = true; } else { delete this.reviewSelection[p.id]; }
		});
	}

	reviewSelectedRows(): any[] {
		return this.displayedPayables().filter((p: any) => this.isReviewSelected(p));
	}

	reviewSelectedCount(): number {
		return this.reviewSelectedRows().length;
	}

	reviewSelectedTotal(): number {
		return this.reviewSelectedRows().reduce((sum: number, p: any) => sum + (parseFloat(p.balance) || 0), 0);
	}

	reversePayable(p: any): void {
		if (!p || p.id == null || this.reversing || this.bulkReversing) { return; }
		const reason = prompt('Reason for reversal:', 'Statement reconciliation — reversing to re-enter correctly');
		if (reason === null) { return; }
		const trimmed = reason.trim();
		if (!trimmed) { this.note('A reason is required.', 'info'); return; }
		if (!confirm('Reverse V8 ID ' + p.id + ' (' + p.suppreference + ')? This cannot be undone from here.')) { return; }

		this.reversing = true;
		this.vendorService.reverseTransaction(p.id, trimmed).subscribe((res: any) => {
			this.reversing = false;
			if (res && res.success) {
				this.note('Reversed.', 'success');
				delete this.reviewSelection[p.id];
				this.loadPayables();
				if (this.run) { this.rescan(); }
			} else {
				this.note((res && res.error) ? res.error : 'Reversal failed.', 'error');
			}
		}, () => { this.reversing = false; this.note('Reversal failed.', 'error'); });
	}

	bulkReverseSelected(): void {
		const rows = this.reviewSelectedRows();
		if (!rows.length || this.bulkReversing) { return; }
		const reason = prompt('Reason for reversing all ' + rows.length + ' selected row(s):', 'Statement reconciliation — reversing to re-enter correctly');
		if (reason === null) { return; }
		const trimmed = reason.trim();
		if (!trimmed) { this.note('A reason is required.', 'info'); return; }
		if (!confirm('Reverse ' + rows.length + ' V8 payable(s) totaling ' + this.money(this.reviewSelectedTotal()) +
			'? Only reverse rows you\'ve verified are entry errors — this cannot be undone from here.')) { return; }

		let ok = 0, failed = 0;
		this.bulkReversing = true;
		const next = (idx: number) => {
			if (idx >= rows.length) {
				this.bulkReversing = false;
				this.reviewSelection = {};
				this.note('Reversed ' + ok + ' row(s)' + (failed ? '; ' + failed + ' failed.' : '.'), failed ? 'warning' : 'success');
				this.loadPayables();
				if (this.run) { this.rescan(); }
				return;
			}
			this.vendorService.reverseTransaction(rows[idx].id, trimmed).subscribe((res: any) => {
				if (res && res.success) { ok++; } else { failed++; }
				next(idx + 1);
			}, () => { failed++; next(idx + 1); });
		};
		next(0);
	}

	/* ----- Ready to pay tab: open payables -> pending payment run (apchecksma/saveforreview) ----- */
	paySelection: any = {};                 // supptrans id -> true
	bankAccounts: any[] = [];
	payBankAcct = '';
	paymentMethods: any[] = [];             // [{paymentid, paymentname}]
	payPaymentType = 'Check';               // paymentname sent as paymentTypes[supplierno]
	payDate: string = new Date().toISOString().slice(0, 10);
	payCheckNum = '1001';
	generatingRun = false;
	generatedRun: { journal: any; total: number; count: number } | null = null;

	private vendorCurrency(): string {
		return (this.vendor && this.vendor.currcode) ? String(this.vendor.currcode).trim() : 'USD';
	}

	loadBankAccounts(): void {
		this.paymentsService.getBankAccountsForCurrency(this.vendorCurrency()).subscribe((res: any) => {
			// Adjustment (__ADJ__) needs a GL code the full AP screen collects — not offered here.
			this.bankAccounts = ((res && res.bankAccounts) || []).filter((b: any) => b.accountcode !== '__ADJ__');
			if (!this.payBankAcct && this.bankAccounts.length === 1) {
				this.payBankAcct = this.bankAccounts[0].accountcode;
			}
		});
		// Same payment-method list the check-printing screen uses; value sent is paymentname.
		this.gss.payment_methods.subscribe((methods: any) => {
			this.paymentMethods = methods || [];
			if (this.payPaymentType && !this.paymentMethods.some((m: any) => m.paymentname === this.payPaymentType)) {
				this.payPaymentType = this.paymentMethods.length ? this.paymentMethods[0].paymentname : this.payPaymentType;
			}
		});
	}

	/** Ready to pay = open in V8, matched to this statement, and (when the cutoff
	 *  is set) due on/before it. Suspect rows (open, due, but NOT on the statement)
	 *  stay on the review tab instead. */
	payDueCutoff = '';
	readyRows(): any[] {
		return (this.payables || []).filter((p: any) =>
			Math.abs(parseFloat(p.balance) || 0) > 0.009 && p.on_statement === 'Yes' &&
			(!this.payDueCutoff || (p.duedate && String(p.duedate).slice(0, 10) <= this.payDueCutoff)));
	}

	/** Everything starts selected — the reviewer unchecks what to hold back. */
	enterReadyTab(): void {
		this.subTab = 'ready';
		if (!Object.keys(this.paySelection).length) {
			this.readyRows().forEach((p: any) => { this.paySelection[p.id] = true; });
		}
	}

	isPaySelected(p: any): boolean {
		return !!(p && p.id != null && this.paySelection[p.id]);
	}

	setPaySelected(p: any, ev: any): void {
		if (!p || p.id == null) { return; }
		if (ev && ev.target && ev.target.checked) {
			this.paySelection[p.id] = true;
		} else {
			delete this.paySelection[p.id];
		}
	}

	allReadySelected(): boolean {
		const rows = this.readyRows();
		return rows.length > 0 && rows.every((p: any) => this.isPaySelected(p));
	}

	toggleAllReady(ev: any): void {
		const checked = !!(ev && ev.target && ev.target.checked);
		this.readyRows().forEach((p: any) => {
			if (checked) { this.paySelection[p.id] = true; } else { delete this.paySelection[p.id]; }
		});
	}

	paySelectedRows(): any[] {
		return this.readyRows().filter((p: any) => this.isPaySelected(p));
	}

	paySelectedTotal(): number {
		return this.paySelectedRows().reduce((sum: number, p: any) => sum + (parseFloat(p.balance) || 0), 0);
	}

	generatePaymentRun(): void {
		const rows = this.paySelectedRows();
		if (!rows.length || this.generatingRun) { return; }
		if (!this.payBankAcct) { this.note('Pick a bank account first.', 'info'); return; }
		const total = this.paySelectedTotal();
		if (!confirm('Create a payment run for ' + rows.length + ' transaction(s), ' +
			this.money(total) + ' ' + this.vendorCurrency() + '?')) { return; }

		const include: any = {}, payamt: any = {};
		rows.forEach((p: any) => {
			include[p.id] = true;
			payamt[p.id] = parseFloat(p.balance) || 0;
		});
		const payload: any = {
			reprint: 'false',
			journal: '',
			checkNum: (this.payCheckNum || '1001').trim(),
			date: { [this.supplierno]: this.payDate },
			payamt,
			discamt: {},
			bankacct: this.payBankAcct,
			currency: this.vendorCurrency(),
			override_currency: null,
			duedate: this.payDate,
			include,
			vendorCheckNumbers: {},
			// Server keys paymentTypes by supplierno and resolves it against paymentmethods
			// (paymentname or paymentid); empty -> defaults to Check.
			paymentTypes: this.payPaymentType ? { [this.supplierno]: this.payPaymentType } : {}
		};

		this.generatingRun = true;
		this.paymentsService.savePaymentRunForReview(payload).subscribe((res: any) => {
			this.generatingRun = false;
			if (res && res.success) {
				this.generatedRun = { journal: res.journal, total, count: rows.length };
				this.paySelection = {};
				this.note('Payment run #' + res.journal + ' created — opening for review.', 'success');
				this.router.navigate(['/vendors-payment-run-review/' + res.journal]);
			} else {
				this.note((res && res.error) ? res.error : 'Could not create the payment run.', 'error');
			}
		}, () => { this.generatingRun = false; this.note('Could not create the payment run.', 'error'); });
	}

	loadRun(runId: number): void {
		this.loadingRun = true;
		this.vendorService.stmtGetRun(runId).subscribe((r: any) => {
			this.loadingRun = false;
			if (r && r.success) {
				this.run = r.run;
				this.subTab = 'summary';
				this.clearSelectedLines();
				this.payDueCutoff = (this.run && this.run.due_date) ? String(this.run.due_date).slice(0, 10) : '';
				this.loadIssues(runId);
				this.loadFixes(runId);
				this.loadPayables();   // re-load with matchback against this run
			}
		}, () => { this.loadingRun = false; });
	}
	rescanning = false;
	rescan(): void {
		if (!this.run || this.rescanning) { return; }
		this.rescanning = true;
		this.vendorService.stmtRescan(this.run.id).subscribe((res: any) => {
			this.rescanning = false;
			if (res && res.success) {
				this.note('Rescanned against current V8 — totals and issue statuses refreshed.', 'success');
				this.loadRun(this.run.id);
				this.loadLines(this.activeIssue);
			} else {
				this.note((res && res.error) ? res.error : 'Rescan failed.', 'error');
			}
		}, () => { this.rescanning = false; this.note('Rescan failed.', 'error'); });
	}
	loadIssues(runId: number): void {
		this.loadingIssues = true;
		this.vendorService.stmtIssueSummary(runId).subscribe((r: any) => {
			this.loadingIssues = false;
			this.issues = (r && r.issues) || [];
		}, () => { this.loadingIssues = false; });
	}
	totalIssueCount(): number {
		// clean = nothing wrong; pre_cutover = out of scope, not work
		return (this.issues || []).filter((i: any) => i.issue !== 'clean' && i.issue !== 'pre_cutover')
			.reduce((sum: number, i: any) => sum + (parseInt(i.n, 10) || 0), 0);
	}
	loadFixes(runId: number): void {
		this.loadingFixes = true;
		this.vendorService.stmtFixes(runId).subscribe((r: any) => {
			this.loadingFixes = false;
			this.fixes = (r && r.fixes) || [];
		}, () => { this.loadingFixes = false; });
	}
	loadLines(issue: string | null): void {
		if (!this.run) {
			return;
		}
		this.activeIssue = issue;
		this.loadingLines = true;
		this.vendorService.stmtLines(this.run.id, issue || undefined).subscribe((r: any) => {
			this.loadingLines = false;
			this.lines = (r && r.lines) || [];
			this.clearSelectedLines();
		}, () => { this.loadingLines = false; });
	}

	showSuggestions = false;     // default: keep the team's view simple ("Not in V8")
	confLabels: any = { likely_typo: 'likely typo', weak: 'weak match', none: '' };

	/** Apply is only ever offered on a REAL matched V8 row, never on a suggestion. */
	canApply(line: any): boolean {
		if (!line || !line.erp_supptrans_id) return false;
		const a = line.proposed_action;
		return a === 'due_date_fix' || a === 'change_ref' || a === 'reopen_owner';
	}

	/** Parsed action_detail (suggestion info for not-in-V8 rows). */
	detail(line: any): any {
		try { return line && line.action_detail ? JSON.parse(line.action_detail) : {}; } catch (e) { return {}; }
	}
	hasSuggestion(line: any): boolean {
		const d = this.detail(line);
		return !!(d && d.suggest_ref);
	}
	highlightCell(line: any, cell: string): boolean {
		if (!line) return false;
		// out of scope — nothing on a pre-cutover row is actionable
		if (line.issue === 'pre_cutover') return false;
		// A row gets ONE issue for the tabs, but it can be wrong two ways at once.
		// Flag both regardless of which one it filed under, or the second problem
		// rides along invisibly and the magic button "fixes" the row with it still there.
		if ((cell === 'stmt_due' || cell === 'v8_due') && Number(line.due_match) === 0) return true;
		if ((cell === 'stmt_amt' || cell === 'v8_gross') && Number(line.amt_match) === 0) return true;
		if (line.issue === 'clean') return false;
		const issue = line.issue;
		if (issue === 'not_in_v8') return cell === 'v8_id' || cell === 'v8_ref';
		if (issue === 'booked_reformatted') return cell === 'invoice' || cell === 'v8_ref';
		if (issue === 'duedate_mismatch') return cell === 'stmt_due' || cell === 'v8_due';
		if (issue === 'amount_mismatch') return cell === 'stmt_amt' || cell === 'v8_gross' || cell === 'v8_bal';
		if (issue === 'settled_v8_open_stmt') return cell === 'v8_alloc' || cell === 'v8_bal';
		return false;
	}
	/**
	 * Problems this row ALSO has beyond the issue it filed under. The tabs stay
	 * one-issue-per-row (deliberately — fewer buckets for the team), so the
	 * second problem has to show on the row itself.
	 */
	alsoFlags(line: any): string[] {
		if (!line || !line.erp_supptrans_id) return [];
		const flags: string[] = [];
		if (Number(line.amt_match) === 0 && line.issue !== 'amount_mismatch') {
			const v = (line.variance === null || line.variance === undefined) ? null : Number(line.variance);
			flags.push('amount off' + (v === null ? '' : ' ' + (v > 0 ? '+' : '') + v.toFixed(2)));
		}
		if (Number(line.due_match) === 0 && line.issue !== 'duedate_mismatch') {
			flags.push('due date off');
		}
		return flags;
	}

	fixTypeFor(action: string): string {
		if (action === 'due_date_fix') return 'due_date';
		if (action === 'change_ref') return 'ref';
		if (action === 'reopen_owner') return 'reopen';
		return '';
	}

	clearSelectedLines(): void {
		this.selectedLineIds = {};
	}

	isResolved(line: any): boolean {
		return !!line && (line.resolved === true || Number(line.resolved) === 1);
	}

	private lineKey(line: any): string {
		return line && line.id != null ? String(line.id) : '';
	}

	/** Bulk select/apply only works scoped to one issue type (a specific chip), never on "All" —
	 *  different issue types carry different risk (a mechanical due-date fix is safe to mass-apply;
	 *  reopening a settled V8 row is not), so batching across all of them at once is a footgun. */
	selectableLines(): any[] {
		if (!this.activeIssue) { return []; }
		return (this.lines || []).filter((line: any) => !this.isResolved(line) && this.canApply(line));
	}

	isLineSelected(line: any): boolean {
		const key = this.lineKey(line);
		return key !== '' && this.selectedLineIds[key] === true;
	}

	setLineSelected(line: any, ev: any): void {
		const key = this.lineKey(line);
		if (key === '') {
			return;
		}
		const checked = !!(ev && ev.target && ev.target.checked);
		const next = Object.assign({}, this.selectedLineIds);
		if (checked) {
			next[key] = true;
		} else {
			delete next[key];
		}
		this.selectedLineIds = next;
	}

	selectedLines(): any[] {
		return this.selectableLines().filter((line: any) => this.isLineSelected(line));
	}

	selectedCount(): number {
		return this.selectedLines().length;
	}

	allSelectableSelected(): boolean {
		const rows = this.selectableLines();
		return rows.length > 0 && rows.every((line: any) => this.isLineSelected(line));
	}

	toggleAllSelectable(ev: any): void {
		const checked = !!(ev && ev.target && ev.target.checked);
		const next: any = {};
		if (checked) {
			this.selectableLines().forEach((line: any) => {
				const key = this.lineKey(line);
				if (key !== '') {
					next[key] = true;
				}
			});
		}
		this.selectedLineIds = next;
	}

	private fixPayload(line: any): any {
		const fixType = this.fixTypeFor(line.proposed_action);
		if (!fixType) {
			return null;
		}
		const payload: any = { line_id: line.id, fix_type: fixType };
		if (fixType === 'reopen') {
			payload.approve_reopen = true;
		}
		return payload;
	}

	applyFix(line: any): void {
		const fixType = this.fixTypeFor(line.proposed_action);
		if (!fixType) {
			this.note('This row is a proposal — handle it in the booking workflow.', 'info');
			return;
		}
		if (fixType === 'reopen') {
			if (!confirm('Reopen ' + line.stmt_invoice + '? Review how this was paid or allocated first. This will set V8 alloc to 0 and mark it unsettled. Continue?')) {
				return;
			}
		}
		const payload = this.fixPayload(line);
		this.applying = true;
		this.vendorService.stmtApplyFix(payload).subscribe((res: any) => {
			this.applying = false;
			if (res && res.success) {
				this.note('Applied: ' + fixType, 'success');
				this.loadLines(this.activeIssue);
				this.loadIssues(this.run.id);
				this.loadFixes(this.run.id);
			} else {
				this.note((res && res.error) ? res.error : 'Apply failed.', 'error');
			}
		}, () => {
			this.applying = false;
			this.note('Apply failed.', 'error');
		});
	}

	/** Escape hatch for messy V8 rows this tool doesn't have a canned fix for (duplicates, bad
	 *  bookings, etc.) — reverses the matched V8 transaction via the existing AP reversal engine
	 *  (same one used on the vendor transactions screen) so the tech can go re-enter it clean
	 *  themselves. Not gated by canApply()/proposed_action — reversing is independent of whatever
	 *  fix (if any) was suggested, it just needs a real matched V8 row to reverse. */
	reversing = false;
	canReverse(line: any): boolean {
		return !!line && !!line.erp_supptrans_id && !this.isResolved(line);
	}
	reverseLine(line: any): void {
		if (!this.canReverse(line) || this.reversing) { return; }
		const reason = prompt('Reason for reversal:', 'Statement reconciliation — reversing to re-enter correctly');
		if (reason === null) { return; }
		const trimmed = reason.trim();
		if (!trimmed) { this.note('A reason is required.', 'info'); return; }
		if (!confirm('Reverse V8 ID ' + line.erp_supptrans_id + ' (' + line.stmt_invoice + ')? This cannot be undone from here.')) { return; }

		this.reversing = true;
		this.vendorService.reverseTransaction(line.erp_supptrans_id, trimmed).subscribe((res: any) => {
			this.reversing = false;
			if (res && res.success) {
				this.note('Reversed.', 'success');
				this.rescan();
			} else {
				this.note((res && res.error) ? res.error : 'Reversal failed.', 'error');
			}
		}, () => { this.reversing = false; this.note('Reversal failed.', 'error'); });
	}

	applySelectedFixes(): void {
		const rows = this.selectedLines();
		if (!rows.length) {
			this.note('Select at least one applyable row.', 'info');
			return;
		}
		const reopenRows = rows.filter((line: any) => this.fixTypeFor(line.proposed_action) === 'reopen');
		if (reopenRows.length && !confirm('Reopen ' + reopenRows.length + ' settled V8 row(s)? Review how they were paid or allocated first. This will set V8 alloc to 0 and mark them unsettled. Continue?')) {
			return;
		}

		let ok = 0, failed = 0;
		this.applying = true;
		const next = (idx: number) => {
			if (idx >= rows.length) {
				this.applying = false;
				this.clearSelectedLines();
				this.note('Applied ' + ok + ' fix(es)' + (failed ? '; ' + failed + ' failed.' : '.'), failed ? 'warning' : 'success');
				this.loadLines(this.activeIssue);
				this.loadIssues(this.run.id);
				this.loadFixes(this.run.id);
				return;
			}
			const payload = this.fixPayload(rows[idx]);
			if (!payload) {
				failed++;
				next(idx + 1);
				return;
			}
			this.vendorService.stmtApplyFix(payload).subscribe((res: any) => {
				if (res && res.success) {
					ok++;
				} else {
					failed++;
				}
				next(idx + 1);
			}, () => {
				failed++;
				next(idx + 1);
			});
		};
		next(0);
	}

	exporting = false;
	private activeExportTab(): string {
		if (this.subTab === 'payables' && this.payablesReviewOnly) return 'review';
		return this.subTab || 'summary';
	}
	exportWorkbook(tabOnly = false): void {
		if (!this.run) return;
		this.exporting = true;
		this.vendorService.stmtExport(this.run.id, tabOnly ? this.activeExportTab() : 'all').subscribe((res: any) => {
			this.exporting = false;
			if (res && res.success && res.content) {
				try { this.gss.downloadXls(res.content, res.filename); } catch (e) { this.note('Could not start download.', 'error'); }
			} else { this.note((res && res.error) ? res.error : 'Export failed.', 'error'); }
		}, () => { this.exporting = false; this.note('Export failed.', 'error'); });
	}

	/** PDF via the app's print/pdf (wkhtmltopdf), built from the staged run — deterministic. */
	exportingPdf = false;
	exportPdf(tabOnly = false): void {
		if (!this.run) return;
		this.exportingPdf = true;
		// Ensure the PDF uses complete staged data, not just the current visible filter.
		this.vendorService.stmtLines(this.run.id).subscribe((lr: any) => {
			this.lines = (lr && lr.lines) || this.lines || [];
			this.vendorService.stmtPayables(this.supplierno, false, this.run.id).subscribe((pr: any) => {
				this.payables = (pr && pr.payables) || this.payables || [];
				this.matchback = (pr && pr.matchback) || this.matchback;
				const tab = tabOnly ? this.activeExportTab() : 'all';
				const filename = (this.vendorLabel() || this.supplierno) + '_statement_' + (this.run.statement_date || '') + (tabOnly ? '_' + tab : '') + '.pdf';
				const html = this.buildPdfHtml(tab);
				this.printService.pdf({ content: this.gss.base64encode(html), filename, landscape: true }).subscribe(
					(res: any) => {
						this.exportingPdf = false;
						if (res && res.content) { this.gss.downloadPdf(res.content, filename); }
						else { this.note('Could not generate PDF.', 'error'); }
					},
					() => { this.exportingPdf = false; this.note('Could not generate PDF.', 'error'); }
				);
			}, () => { this.exportingPdf = false; this.note('Could not generate PDF.', 'error'); });
		}, () => { this.exportingPdf = false; this.note('Could not generate PDF.', 'error'); });
	}

	private vendorLabel(): string { return (this.vendor && (this.vendor.suppname || this.vendor.vendor_label)) || this.supplierno; }
	private money(n: any): string { const v = Number(n) || 0; return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

	/** Static, repeatable HTML for the PDF: the same sections as the visible tabs. */
	private buildPdfHtml(tab = 'all'): string {
		const r = this.run, bt = r.bucket_totals || {};
		const esc = (s: any) => String(s == null ? '' : s).replace(/[&<>]/g, (c: string) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' } as any)[c]);
		const allLines = this.lines || [];
		const prob = allLines.filter((l: any) => l.issue !== 'clean' && l.issue !== 'pre_cutover');
		const review = (this.payables || []).filter((p: any) => p.review == 1);
		const payables = this.payables || [];
		const issueRows = this.issues.map((i: any) =>
			`<tr><td>${esc(this.issueLabels[i.issue] || i.issue)}</td><td>${esc(this.actionLabels[i.proposed_action] || i.proposed_action)}</td><td class=r>${i.n}</td><td class=r>${this.money(i.amt)}</td></tr>`).join('');
		const lineRow = (l: any) => {
			const d = this.detail(l);
			const sugg = d && d.suggest_ref ? `${this.confLabels[d.confidence] || ''}: ${esc(d.suggest_ref)} — ${esc(d.reason)}` : '';
			return `<tr><td>${esc(l.stmt_invoice)}</td><td>${esc(l.doc_date)}</td><td>${esc(l.due_date)}</td><td>${esc(l.erp_due_date)}</td><td class=r>${this.money(l.amount)}</td><td>${esc(l.erp_supptrans_id)}</td><td>${esc(l.erp_transno)}</td><td>${esc(l.erp_ref)}</td><td>${esc(this.issueLabels[l.issue] || l.issue)}</td><td>${esc(this.actionLabels[l.proposed_action] || l.proposed_action)}</td><td class=sm>${sugg}</td></tr>`;
		};
		const rawRows = allLines.map(lineRow).join('');
		const probRows = prob.map(lineRow).join('');
		const reviewRows = review.map((p: any) =>
			`<tr><td>${esc(p.id)}</td><td>${esc(p.transno)}</td><td>${esc(p.checknum)}</td><td>${esc(p.suppreference)}</td><td>${esc(p.duedate)}</td><td class=r>${this.money(p.balance)}</td></tr>`).join('');
		const payableRows = payables.map((p: any) =>
			`<tr><td>${esc(p.id)}</td><td>${esc(p.transno)}</td><td>${esc(p.checknum)}</td><td>${esc(p.suppreference)}</td><td>${esc(p.duedate)}</td><td class=r>${this.money(p.total)}</td><td class=r>${this.money(p.applied)}</td><td class=r>${this.money(p.balance)}</td><td>${esc(p.on_statement)}</td></tr>`).join('');
		const lineHeader = '<tr><th>Invoice</th><th>Doc</th><th>Stmt due</th><th>V8 due</th><th class=r>Stmt amt</th><th>V8 ID</th><th>V8 trans#</th><th>V8 ref</th><th>Issue</th><th>Action</th><th>Closest V8 (verify)</th></tr>';
		const payableHeader = '<tr><th>V8 ID</th><th>V8 trans#</th><th>Payment/check</th><th>Reference</th><th>Due</th><th class=r>Total</th><th class=r>Applied</th><th class=r>Balance</th><th>On statement?</th></tr>';
		const reviewHeader = '<tr><th>V8 ID</th><th>V8 trans#</th><th>Payment/check</th><th>Reference</th><th>Due</th><th class=r>Balance</th></tr>';
		const issuesTable = `<table>${lineHeader}${probRows}</table>`;
		const rawTable = `<table>${lineHeader}${rawRows}</table>`;
		const payablesTable = `<table>${payableHeader}${payableRows}</table>`;
		const reviewTable = `<table>${reviewHeader}${reviewRows}</table>`;
		const st = `<style>
			body{font-family:Arial,Helvetica,sans-serif;color:#101828;font-size:11px}
			h1{font-size:16px;color:#1f2a44;margin:0 0 2px} .sub{color:#667085;font-size:11px;margin:0 0 12px}
			h2{font-size:13px;color:#1f2a44;border-left:3px solid #2563eb;padding-left:7px;margin:16px 0 6px}
			table{width:100%;border-collapse:collapse;margin-bottom:8px} th{background:#1f2a44;color:#fff;text-align:left;padding:5px 7px;font-size:10px}
			td{padding:4px 7px;border-bottom:1px solid #eef0f3} .r{text-align:right} .sm{color:#667085;font-size:10px}
			.kpis{margin:0 0 10px} .kpi{display:inline-block;border:1px solid #e4e7ec;border-radius:6px;padding:6px 12px;margin:0 8px 6px 0}
			.kpi b{font-size:15px;display:block} .kpi span{font-size:9px;color:#667085;text-transform:uppercase}
			.section{page-break-before:always}.section:first-of-type{page-break-before:auto}
		</style>`;
		const head = `${st}<h1>${esc(this.vendorLabel())} — statement reconciliation</h1>
			<p class=sub>Statement ${esc(r.statement_date)} · due ${esc(r.due_date)} · supplier ${esc(this.supplierno)}</p>
			`;
		if (tab === 'raw') {
			return `${head}<h2>Raw statement</h2>${rawTable}`;
		}
		if (tab === 'issues') {
			return `${head}<h2>Issues & actions</h2>${issuesTable}`;
		}
		if (tab === 'payables') {
			return `${head}<h2>V8 Payables</h2>${payablesTable}`;
		}
		if (tab === 'review') {
			return `${head}<h2>In V8, not on statement</h2>${reviewTable}`;
		}
		const summaryHtml = `${head}
			<div class=kpis>
				<div class=kpi><span>Statement total</span><b>$${this.money(r.parsed_total)}</b></div>
				<div class=kpi><span>Amount due now</span><b>$${this.money(r.amount_due_now)}</b></div>
				<div class=kpi><span>Past due</span><b>$${this.money(bt.past_due)}</b></div>
				<div class=kpi><span>Current</span><b>$${this.money(bt.current)}</b></div>
				<div class=kpi><span>Future</span><b>$${this.money(bt.future)}</b></div>
				${bt.pre_cutover ? `<div class=kpi><span>Pre-cutover (carried)</span><b>$${this.money(bt.pre_cutover)}</b></div>` : ''}
			</div>
			<h2>Issues summary</h2>
			<table><tr><th>Issue</th><th>Action</th><th class=r>Count</th><th class=r>Amount</th></tr>${issueRows}</table>`;
		if (tab === 'summary') {
			return summaryHtml;
		}
		return `<div class=section>${summaryHtml}</div>
			<div class=section><h2>Issues & actions</h2>${issuesTable}</div>
			<div class=section><h2>Raw statement</h2>${rawTable}</div>
			<div class=section><h2>V8 Payables</h2>${payablesTable}</div>
			<div class=section><h2>In V8, not on statement</h2>${reviewTable}</div>`;
	}

	rollback(fix: any): void {
		if (!confirm('Roll back this ' + fix.fix_type + ' fix (restore the original V8 values)?')) return;
		this.vendorService.stmtRollbackFix({ fix_id: fix.id }).subscribe((res: any) => {
			if (res && res.success) { this.note('Rolled back.', 'success'); this.loadFixes(this.run.id); this.loadIssues(this.run.id); }
			else { this.note((res && res.error) ? res.error : 'Rollback failed.', 'error'); }
		});
	}

	private note(msg: string, type: string): void {
		try { this.gss.showNotification(msg, type, 'bottom', 'right'); } catch (e) { /* noop */ }
	}
}
